import "../styles/ux-refinements.css";
import { GuestPrivacyActions } from "../pages/more/guest-privacy-actions.js";
import { useState, type FormEvent } from "react";
import {
  authenticationLinkContext,
  hasAuthenticationLink,
} from "../lib/invite-link.js";
import { DesktopLanding } from "../shell/desktop-landing.js";
import { authClient } from "./auth-client.js";
import {
  AuthCard,
  AuthField,
  EyeToggle,
  LockIcon,
  MailIcon,
  PersonIcon,
} from "./auth-card.js";
import {
  PasswordResetCompletion,
  PasswordResetRequest,
} from "./password-reset-screen.js";

type Mode = "sign-in" | "register";
type AuthFormModel = ReturnType<typeof useAuthForm>;

export function AuthScreen(
  props: { recoveryEmail?: string; onContinueSavedRound?(): void } = {},
): React.JSX.Element {
  const token = new URLSearchParams(window.location.search).get("token");
  const [resetRequested, setResetRequested] = useState(false);
  const form = useAuthForm(props.recoveryEmail);
  if (token) return <PasswordResetCompletion token={token} />;
  if (resetRequested)
    return <PasswordResetRequest onBack={() => setResetRequested(false)} />;
  const register = form.mode === "register";
  const context = authenticationLinkContext(window.location.search);
  return (
    <>
      <DesktopLanding />
      <AuthCard
        context={register ? "register" : undefined}
        eyebrow={register ? "Join the Clubhouse" : "Your Clubhouse awaits"}
        title={
          context?.title ??
          (register ? "Create your account." : "Welcome back.")
        }
        intro={
          context?.intro ??
          (register
            ? "Create your account with an email and password."
            : "Score with your friends. Keep the story after the last hole.")
        }
      >
        {form.message.startsWith("Check your email") ? (
          <section className="fr-auth-verification" role="status">
            <h2>Check your email</h2>
            <p>
              Open the verification link we sent to{" "}
              {form.email || "your email address"}. Then return here to sign in.
              Check your spam folder if it has not arrived.
            </p>
          </section>
        ) : null}
        <AuthForm form={form} recoveryEmail={props.recoveryEmail} />
        {register ? null : (
          <button
            type="button"
            className="fr-auth-link"
            onClick={() => setResetRequested(true)}
          >
            Forgot password?
          </button>
        )}
        {props.onContinueSavedRound ? (
          <button
            type="button"
            className="fr-auth-link"
            onClick={props.onContinueSavedRound}
          >
            Continue saved round
          </button>
        ) : null}
        {form.message && !form.message.startsWith("Check your email") ? (
          <p className="fr-auth-msg" data-tone={form.tone} role="status">
            {form.message}
          </p>
        ) : null}
        <footer className="fr-auth-footer">
          <GuestPrivacyActions />
        </footer>
      </AuthCard>
    </>
  );
}

/** Credential fields plus the submit and mode-switch actions. */
function AuthForm(props: {
  form: AuthFormModel;
  recoveryEmail?: string;
}): React.JSX.Element {
  const { form } = props;
  const register = form.mode === "register";
  return (
    <form onSubmit={(event) => void form.submit(event)}>
      {register ? (
        <AuthField
          label="Your name"
          hint="— how rivals will see you"
          icon={<PersonIcon />}
        >
          <input
            autoComplete="name"
            maxLength={80}
            required
            placeholder="Your name"
            value={form.name}
            onChange={(event) => form.setName(event.target.value)}
          />
        </AuthField>
      ) : null}
      <AuthField label="Email" icon={<MailIcon />}>
        <input
          autoComplete="email"
          inputMode="email"
          required
          type="email"
          placeholder="you@example.com"
          readOnly={Boolean(props.recoveryEmail)}
          value={form.email}
          onChange={(event) => form.setEmail(event.target.value)}
        />
      </AuthField>
      <AuthField
        label="Password"
        hint={register ? "— min. 10 characters" : undefined}
        icon={<LockIcon />}
        trailing={
          <EyeToggle shown={form.showPassword} onToggle={form.toggleShow} />
        }
      >
        <input
          autoComplete={register ? "new-password" : "current-password"}
          minLength={register ? 10 : undefined}
          required
          type={form.showPassword ? "text" : "password"}
          placeholder="Password"
          value={form.password}
          onChange={(event) => form.setPassword(event.target.value)}
        />
      </AuthField>
      <AuthActions form={form} recoveryEmail={props.recoveryEmail} />
    </form>
  );
}

function AuthActions(props: {
  form: AuthFormModel;
  recoveryEmail?: string;
}): React.JSX.Element {
  const { form } = props;
  const register = form.mode === "register";
  return (
    <div className="fr-auth-actions">
      <button
        type="submit"
        className="fr-btn"
        data-variant="gold-gradient"
        data-size="lg"
        disabled={form.pending}
      >
        {form.pending
          ? "Please wait…"
          : register
            ? "Create account"
            : "Sign in"}
      </button>
      {props.recoveryEmail ? null : (
        <button
          type="button"
          className="fr-auth-link fr-auth-switch"
          onClick={() => form.setMode(register ? "sign-in" : "register")}
        >
          {register ? "← Back to sign in" : "Create account"}
        </button>
      )}
    </div>
  );
}

function useAuthForm(initialEmail = "") {
  const [mode, setMode] = useState<Mode>("sign-in");
  const [name, setName] = useState("");
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState(
    () => sessionStorage.getItem("fairway-rivalry:auth-message") ?? "",
  );
  const [tone, setTone] = useState<"ok" | "error">("ok");
  const [pending, setPending] = useState(false);
  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setPending(true);
    setMessage("");
    sessionStorage.removeItem("fairway-rivalry:auth-message");
    const result =
      mode === "register"
        ? await authClient.signUp.email({
            name: name.trim(),
            email: email.trim(),
            password,
            callbackURL: callbackPath(),
          })
        : await authClient.signIn.email({
            email: email.trim(),
            password,
            callbackURL: callbackPath(),
          });
    setPending(false);
    if (result.error) {
      setTone("error");
      setMessage(result.error.message ?? "The account request failed.");
      return;
    }
    if (mode === "register") {
      const text =
        "Check your email. Open the verification link, then sign in.";
      sessionStorage.setItem("fairway-rivalry:auth-message", text);
      setTone("ok");
      setMessage(text);
      setMode("sign-in");
      setPassword("");
    }
  };
  return {
    mode,
    setMode: (next: Mode) => {
      setMode(next);
      setMessage("");
    },
    name,
    setName,
    email,
    setEmail,
    password,
    setPassword,
    showPassword,
    toggleShow: () => setShowPassword((value) => !value),
    message,
    tone,
    pending,
    submit,
  };
}

function callbackPath(): string {
  return hasAuthenticationLink(window.location.search)
    ? window.location.pathname + window.location.search
    : "/";
}
